import React from 'react';
import { CalendarClock } from 'lucide-react';
import { InfoTooltip } from '../InfoTooltip';

import { ACTIVITY_ICON_MAP } from '../../utils/activityIcons';
import { AppSelectDropdown } from '../AppSelectDropdown';
import { DatePicker } from '../DatePicker';
import { EmployeeMultiSelect, EINSATZTEAM_MULTISELECT_LABELS, type EmployeeMultiSelectOption } from '../employees/EmployeeMultiSelect';
import { UserMultiSelect, PROJEKTLEITER_MULTISELECT_LABELS, type UserMultiSelectOption } from '../UserMultiSelect';
import { SCHEDULING_STATUS_LABELS, type SchedulingStatus } from '../plantafel/plantafelTypes';
import { nextChecklistIdForActivityTypeChange } from '../../utils/activityTypeDefaultChecklist';
import {
  APP_INPUT_CLASS,
  APP_SELECT_TRIGGER_CLASS,
  APP_TEXTAREA_CLASS,
} from '../../ui/controlStyles';
import { UI_Z_INDEX } from '../../utils/uiZIndex';
import {
  ActivityRecurrenceFields,
  defaultRecurrenceForm,
  multiDaySeriesHint,
} from './ActivityRecurrenceFields';
import {
  applyRecurrenceChoice,
  autoMultiDaySeriesEnd,
  reconcileMultiDaySeries,
  type MultiDaySeriesState,
  type RecurrenceFormState,
} from '../../utils/activityRecurrence';

export type ProjectActivityFormState = {
  title: string;
  activity_type: string;
  checklist_id: string | null;
  scheduling_status: SchedulingStatus;
  dueDate: string;
  dueTimeStart: string;
  dueTimeEnd: string;
  endDate: string;
  notes: string;
  assignee_user_ids: string[];
  assignee_employee_ids: string[];
  recurrence?: RecurrenceFormState;
  multiDaySeries?: MultiDaySeriesState;
};

type ActivityTypeOption = { id: string; label: string; icon: string; default_checklist_id?: string | null };

const SCHEDULING_STATUS_OPTIONS = (
  Object.entries(SCHEDULING_STATUS_LABELS) as [SchedulingStatus, string][]
).map(([value, label]) => ({ value, label }));

function ActivityRequiredMark() {
  return <span className="text-red-500"> *</span>;
}

type ProjectActivityFormFieldsProps = {
  form: ProjectActivityFormState;
  setForm: React.Dispatch<React.SetStateAction<ProjectActivityFormState>>;
  activityTypesList: ActivityTypeOption[];
  checklistOptions: { value: string; label: string }[];
  userSelectOptions: UserMultiSelectOption[];
  employeeSelectOptions: EmployeeMultiSelectOption[];
  addressField: React.ReactNode;
  /** Native `<input type="date">` in modals; DatePicker in the project tab. */
  dateInputVariant?: 'picker' | 'native';
  descriptionRows?: number;
  compactTypePills?: boolean;
  /** Mobile: changing the start date always updates the end date too. */
  syncEndDateWithStartDate?: boolean;
  /** z-index for portaled pickers when the form sits in a modal. */
  dropdownZIndex?: number;
};

export function ProjectActivityFormFields({
  form,
  setForm,
  activityTypesList,
  checklistOptions,
  userSelectOptions,
  employeeSelectOptions,
  addressField,
  dateInputVariant = 'picker',
  descriptionRows = 3,
  compactTypePills = false,
  syncEndDateWithStartDate = false,
  dropdownZIndex,
}: ProjectActivityFormFieldsProps) {
  const pickerZ = dropdownZIndex ?? UI_Z_INDEX.portalDropdown;
  const dateInputClass = APP_INPUT_CLASS;

  const dateField = (value: string, onChange: (v: string) => void, placeholder?: string) =>
    dateInputVariant === 'picker' ? (
      <DatePicker
        value={value}
        onChange={onChange}
        className="w-full min-w-0"
        placeholder={placeholder ?? 'TT.MM.JJJJ'}
        menuZIndex={pickerZ}
      />
    ) : (
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={dateInputClass} />
    );

  const typePillClass = (active: boolean) =>
    compactTypePills
      ? `inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
          active ? 'bg-pipedrive-blue text-white' : 'bg-app-surface-muted text-app-text-muted'
        }`
      : `inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors ${
          active ? 'bg-pipedrive-blue text-white' : 'bg-app-surface-muted text-app-text-muted hover:bg-app-hover'
        }`;

  const typeIconClass = compactTypePills ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const fieldLabelClass = 'mb-1 block text-xs font-medium text-app-text-muted';
  const recurrence = form.recurrence ?? defaultRecurrenceForm(form.dueDate);
  const repeating = recurrence.freq !== 'none';
  const autoSeries = autoMultiDaySeriesEnd(form) !== null;
  /** Date/time edits: multi-day spans (> 24 h) become a series, and back when they no longer fit. */
  const setSpan = (update: (f: ProjectActivityFormState) => ProjectActivityFormState) =>
    setForm((f) => reconcileMultiDaySeries(update(f)));

  return (
    <div className="w-full min-w-0 space-y-4">
      <div>
        <label className={fieldLabelClass}>
          Titel
          <ActivityRequiredMark />
        </label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="z. B. Anruf mit Kunde, Rückruf vereinbart"
          className={APP_INPUT_CLASS}
        />
      </div>

      <div>
        <p className={fieldLabelClass}>Typ</p>
        <div className={`flex flex-wrap ${compactTypePills ? 'gap-1.5' : 'gap-2'}`}>
          {activityTypesList.map(({ id, label, icon }) => {
            const Icon = ACTIVITY_ICON_MAP[icon] ?? CalendarClock;
            return (
              <button
                key={id}
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    activity_type: id,
                    checklist_id: nextChecklistIdForActivityTypeChange(
                      activityTypesList,
                      f.activity_type,
                      id,
                      f.checklist_id,
                    ),
                  }))
                }
                className={typePillClass(form.activity_type === id)}
              >
                <Icon className={typeIconClass} /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Zeitraum: Beginn and Ende side by side, each date + time (stacks on narrow screens). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <label className={fieldLabelClass}>Beginn</label>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              {dateField(form.dueDate, (v) =>
                setSpan((f) => ({
                  ...f,
                  dueDate: v,
                  endDate:
                    repeating || syncEndDateWithStartDate || f.endDate === f.dueDate ? v : f.endDate,
                })),
              )}
            </div>
            <input
              type="time"
              aria-label="Beginn Uhrzeit"
              value={form.dueTimeStart}
              onChange={(e) => setSpan((f) => ({ ...f, dueTimeStart: e.target.value }))}
              className={`${APP_INPUT_CLASS} !w-28 shrink-0`}
            />
          </div>
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClass}>Ende</label>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              {repeating ? (
                // Each occurrence ends on its own day; the series end is set below.
                <p className="flex h-9 items-center px-3 text-sm text-app-text-subtle">Am selben Tag</p>
              ) : (
                dateField(form.endDate, (v) =>
                  // Picking an end date again re-enables the automatic series.
                  setSpan((f) => ({ ...f, endDate: v, multiDaySeries: null })),
                )
              )}
            </div>
            <input
              type="time"
              aria-label="Ende Uhrzeit"
              value={form.dueTimeEnd}
              onChange={(e) => setSpan((f) => ({ ...f, dueTimeEnd: e.target.value }))}
              className={`${APP_INPUT_CLASS} !w-28 shrink-0`}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <label className={fieldLabelClass}>Status</label>
          <AppSelectDropdown
            className="w-full"
            value={form.scheduling_status}
            onChange={(value) => setForm((f) => ({ ...f, scheduling_status: value as SchedulingStatus }))}
            options={SCHEDULING_STATUS_OPTIONS}
            triggerClassName={APP_SELECT_TRIGGER_CLASS}
            menuZIndex={pickerZ}
          />
        </div>
        <div className="min-w-0">
          <ActivityRecurrenceFields
            form={recurrence}
            onChange={(next) => setForm((f) => applyRecurrenceChoice(f, next))}
            hint={autoSeries ? multiDaySeriesHint(recurrence) : null}
            startYmd={form.dueDate}
            menuZIndex={pickerZ}
          />
        </div>
        {repeating ? (
          <div className="min-w-0 sm:col-start-2">
            <label className={fieldLabelClass}>Serie endet am</label>
            {dateField(recurrence.untilDate, (v) =>
              setSpan((f) => ({
                ...f,
                recurrence: { ...recurrence, untilDate: v, endMode: 'until' },
                ...(autoMultiDaySeriesEnd(f) ? { multiDaySeries: { endYmd: v } } : {}),
              })),
            )}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <label className={fieldLabelClass}>Projektleiter</label>
          <UserMultiSelect
            users={userSelectOptions}
            selected={form.assignee_user_ids}
            onChange={(v) => setForm((f) => ({ ...f, assignee_user_ids: v }))}
            selectionMode="explicit"
            panelAlign="start"
            className="w-full"
            triggerClassName="inline-flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
            width={320}
            {...PROJEKTLEITER_MULTISELECT_LABELS}
          />
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClass}>Einsatzteam</label>
          <EmployeeMultiSelect
            employees={employeeSelectOptions}
            selected={form.assignee_employee_ids}
            onChange={(v) => setForm((f) => ({ ...f, assignee_employee_ids: v }))}
            selectionMode="explicit"
            panelAlign="start"
            className="w-full"
            triggerClassName="inline-flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
            width={320}
            {...EINSATZTEAM_MULTISELECT_LABELS}
          />
        </div>
      </div>

      {addressField}

      <div className="min-w-0">
        <label className={fieldLabelClass}>Checkliste</label>
        <AppSelectDropdown
          className="w-full"
          value={form.checklist_id ?? ''}
          onChange={(value) => setForm((f) => ({ ...f, checklist_id: value || null }))}
          options={checklistOptions}
          placeholder="Keine Checkliste"
          searchable
          searchPlaceholder="Checkliste suchen…"
          triggerClassName={APP_SELECT_TRIGGER_CLASS}
          menuZIndex={pickerZ}
        />
      </div>

      <div>
        <label className={`${fieldLabelClass} flex items-center gap-1`}>
          Beschreibung
          <InfoTooltip text="Die Beschreibung ist nicht für den Kunden sichtbar." />
        </label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Beschreibung zur Aktivität..."
          rows={descriptionRows}
          className={`${APP_TEXTAREA_CLASS} resize-none`}
        />
      </div>
    </div>
  );
}
